import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wrench, GoogleLogo, ArrowLeft } from "@phosphor-icons/react";

const AUTH_BG = "https://images.unsplash.com/photo-1727893380169-4dda123e19f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBnYXJhZ2UlMjBkaWFnbm9zdGljfGVufDB8fHx8MTc4NTAwOTgzMnww&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Password strength rules — only enforced on signup
  const rules = [
    { key: "len",     label: "At least 8 characters",     ok: password.length >= 8 },
    { key: "upper",   label: "One uppercase letter",      ok: /[A-Z]/.test(password) },
    { key: "number",  label: "One number",                ok: /\d/.test(password) },
    { key: "special", label: "One special character",     ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const passwordOk = rules.every((r) => r.ok);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !passwordOk) {
      toast.error("Please meet all password requirements");
      return;
    }
    if (mode === "signup" && !acceptPrivacy) {
      toast.error("You must agree to the Privacy Policy to create an account.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await register(email, password, name || email.split("@")[0], acceptPrivacy);
        toast.success("Welcome to Dynamic Diag");
      } else {
        await login(email, password);
        toast.success("Signed in");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgotPassword = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter your email above, then click Forgot password?");
      return;
    }
    setForgotSending(true);
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      toast.success("If that email is registered, a reset link is on its way.");
    } catch {
      // Always show the same neutral message — never leak whether an email exists
      toast.success("If that email is registered, a reset link is on its way.");
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid md:grid-cols-2">
      {/* Left panel: form */}
      <div className="p-6 md:p-12 flex flex-col justify-between bg-grain">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors w-fit" data-testid="back-home-link">
          <ArrowLeft weight="bold" className="w-4 h-4" /> Back
        </Link>

        <div className="max-w-md w-full mx-auto py-10 fade-up">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Wrench weight="bold" className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none">DYNAMIC DIAG</div>
              <div className="text-[10px] font-mono-tech text-muted-foreground tracking-widest">AI DIAGNOSTIC ENGINE</div>
            </div>
          </div>

          <h1 className="font-display font-bold text-4xl tracking-tight">
            {mode === "signup" ? "Set up your shop" : "Welcome back"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {mode === "signup" ? "Create an account to save every diagnostic." : "Sign in to pick up where you left off."}
          </p>

          <Button
            type="button"
            onClick={googleSignIn}
            variant="outline"
            className="w-full mt-6 min-h-12"
            data-testid="google-signin-btn"
          >
            <GoogleLogo weight="bold" className="w-5 h-5 mr-2" />
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <div className="text-xs text-muted-foreground font-mono-tech">OR EMAIL</div>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Full name</Label>
                <Input
                  id="name"
                  data-testid="signup-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Torres"
                  className="mt-1 min-h-12 bg-input border-border"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@shop.com"
                className="mt-1 min-h-12 bg-input border-border"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Password</Label>
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={forgotPassword}
                    disabled={forgotSending}
                    className="text-[11px] font-mono-tech uppercase tracking-wider text-primary hover:underline disabled:opacity-50"
                    data-testid="forgot-password-link"
                  >
                    {forgotSending ? "Sending…" : "Forgot password?"}
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                required
                minLength={mode === "signup" ? 8 : 6}
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 min-h-12 bg-input border-border"
              />
              {mode === "signup" && password.length > 0 && (
                <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="password-rules">
                  {rules.map((r) => (
                    <li
                      key={r.key}
                      data-testid={`pw-rule-${r.key}`}
                      className={`text-[11px] font-mono-tech flex items-center gap-1.5 ${r.ok ? "text-green-400" : "text-muted-foreground"}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.ok ? "bg-green-400" : "bg-muted-foreground/50"}`} />
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {mode === "signup" && (
              <label className="flex items-start gap-2 cursor-pointer group" data-testid="privacy-consent-row">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  data-testid="privacy-consent-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-border bg-input accent-primary cursor-pointer"
                />
                <span className="text-xs text-muted-foreground leading-relaxed group-hover:text-white/90 transition-colors">
                  I have read and agree to the{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener"
                    className="text-primary hover:underline"
                    data-testid="privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  . JLDynamic LLC does not sell or share my data; it is used only to run my diagnostics.
                </span>
              </label>
            )}

            <Button
              type="submit"
              disabled={submitting || (mode === "signup" && (!passwordOk || !acceptPrivacy))}
              className="w-full min-h-12 bg-primary hover:bg-primary/90"
              data-testid="auth-submit-btn"
            >
              {submitting ? "Working..." : mode === "signup" ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "signup" ? (
              <>Already have an account?{" "}
                <button className="text-primary hover:underline" onClick={() => setMode("login")} data-testid="switch-to-login">Sign in</button></>
            ) : (
              <>Don't have an account?{" "}
                <button className="text-primary hover:underline" onClick={() => setMode("signup")} data-testid="switch-to-signup">Sign up</button></>
            )}
          </div>
        </div>

        <div className="text-xs text-muted-foreground font-mono-tech">© Dynamic Diag</div>
      </div>

      {/* Right panel: art */}
      <div className="hidden md:block relative border-l border-border">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="border border-border rounded-md p-6 bg-background/70 backdrop-blur max-w-md">
            <div className="font-mono-tech text-xs text-primary mb-2">// SESSION 4823</div>
            <div className="font-display text-2xl leading-tight">
              "Dynamic Diag cuts through the noise. It turns hours of head-scratching troubleshooting into quick, confident repairs."
            </div>
            <div className="text-sm text-muted-foreground mt-3">— Jeff R., Honda Diag Tech</div>
          </div>
        </div>
      </div>
    </div>
  );
}
